import type {Repository} from '@github-ui/current-repository'
import {commitDiscussionsPath} from '@github-ui/paths'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useState} from 'react'

import type {CommitComment} from '../types/commit-types'
import type {Commit} from '../types/shared'

export interface DiscussionCommentData extends StaticDiscussionCommentData {
  comments: CommitComment[]
  commentCount: number
}

interface StaticDiscussionCommentData {
  canLoadMoreComments: boolean
  subscribed: boolean
}

export function useDiscussionComments(commit: Commit, repo: Repository) {
  const [comments, setComments] = useState<CommitComment[]>([])
  const [loaded, setLoaded] = useState(false)
  const [staticData, setStaticData] = useState<StaticDiscussionCommentData | null>(null)
  const [commentCount, setCommentCount] = useState<number | undefined>(undefined)
  const [didError, setDidError] = useState(false)

  const loadDiscussionComments = useCallback(
    async (beforeCommentId?: string) => {
      setLoaded(false)
      setDidError(false)
      const response = await verifiedFetchJSON(
        commitDiscussionsPath({
          owner: repo.ownerLogin,
          repo: repo.name,
          commitOid: commit.oid,
          beforeCommentId,
        }),
      )

      if (response.ok) {
        const data: DiscussionCommentData = await response.json()
        if (beforeCommentId) {
          setComments(c => [...data.comments, ...c])
        } else {
          setComments(data.comments)
        }

        setStaticData({
          canLoadMoreComments: data.canLoadMoreComments,
          subscribed: data.subscribed,
        })
        setCommentCount(data.commentCount)
        setLoaded(true)
        setDidError(false)
      } else {
        setDidError(true)
      }
    },
    [repo.ownerLogin, repo.name, commit.oid],
  )

  useEffect(() => {
    if (!loaded) {
      loadDiscussionComments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDiscussionComments])

  return {
    comments,
    setComments,
    commentCount,
    setCommentCount,
    hasMoreComments: staticData?.canLoadMoreComments,
    subscribed: staticData?.subscribed,
    loadDiscussionComments,
    loaded,
    didError,
  }
}
